import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { RESTAURANT_ID } from '../constants';
import { Card, Empty, Icon, Loading, PageHead, StatusBadge, tl } from '../ui';
import './dashboard.css';

const PERIODS = [
  ['today', 'Bugün'],
  ['week', 'Son 7 gün'],
  ['month', 'Bu ay'],
  ['all', 'Tümü'],
];

const toDate = (o) => o.createdAt?.toDate?.() || null;

function periodStart(period) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (period === 'week') d.setDate(d.getDate() - 6);
  if (period === 'month') d.setDate(1);
  if (period === 'all') return new Date(0);
  return d;
}

// Tek seri dikey çubuk grafik (SVG). Üzerine gelince değer gösterir.
function BarChart({ data, format, height = 180, labelEvery = 1, integer = false, emptyText = 'Henüz veri yok' }) {
  const [hover, setHover] = useState(null);
  const peak = Math.max(0, ...data.map((d) => d.value));
  if (peak === 0) return <Empty icon="trend" title={emptyText} />;
  const max = integer ? Math.max(2, Math.ceil(peak / 2) * 2) : peak;
  const w = 100 / data.length;
  const ticks = [0, 0.5, 1].map((t) => t * max);
  return (
    <div className="kd-chart" style={{ height }}>
      <div className="kd-chart-grid">
        {ticks.slice().reverse().map((t) => <div key={t}><span className="tnum">{format(t)}</span></div>)}
      </div>
      <svg className="kd-chart-svg" viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" role="img" aria-label="Grafik">
        {data.map((d, i) => {
          const h = (d.value / max) * (height - 4);
          const x = i * w + w * 0.18;
          const bw = w * 0.64;
          return (
            <g key={d.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={i * w} y="0" width={w} height={height} fill="transparent" />
              {d.value > 0 && (
                <path
                  className={`kd-bar${hover === i ? ' on' : ''}`}
                  d={`M${x},${height} V${height - h + Math.min(3, h)} Q${x},${height - h} ${x + Math.min(1.2, bw / 2)},${height - h} H${x + bw - Math.min(1.2, bw / 2)} Q${x + bw},${height - h} ${x + bw},${height - h + Math.min(3, h)} V${height} Z`}
                />
              )}
            </g>
          );
        })}
      </svg>
      <div className="kd-chart-x">
        {data.map((d, i) => <span key={d.label} style={{ width: `${w}%` }}>{i % labelEvery === 0 ? d.label : ''}</span>)}
      </div>
      {hover !== null && (
        <div className="kd-tip" style={{ left: `calc(64px + (100% - 64px) * ${(hover + 0.5) / data.length})` }}>
          <strong>{data[hover].label}</strong>
          <span className="tnum">{format(data[hover].value)}</span>
          {data[hover].sub && <em>{data[hover].sub}</em>}
        </div>
      )}
    </div>
  );
}

function HBars({ rows, format }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="kd-hbars">
      {rows.map((r) => (
        <li key={r.label} title={`${r.label}: ${format(r.value)}`}>
          <div className="kd-hbar-top"><span>{r.label}</span><span className="tnum">{format(r.value)}</span></div>
          <div className="kd-hbar-track"><span style={{ width: `${(r.value / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

function Dashboard({ onNavigate }) {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('today');

  const fetchOrders = async () => {
    setError('');
    setOrders(null);
    try {
      const snap = await getDocs(query(collection(db, 'orders'), where('restaurantId', '==', RESTAURANT_ID)));
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      setError('Veriler yüklenemedi. Lütfen tekrar deneyin.');
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const s = useMemo(() => {
    if (!orders) return null;
    const start = periodStart(period);
    const inPeriod = orders.filter((o) => (toDate(o) || 0) >= start);
    const valid = inPeriod.filter((o) => o.status !== 'cancelled');
    const cancelled = inPeriod.filter((o) => o.status === 'cancelled');
    const revenue = valid.reduce((sum, o) => sum + (Number(o.totalPrice) || 0), 0);

    const days = [];
    for (let i = 6; i >= 0; i -= 1) {
      const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - i);
      const next = new Date(d); next.setDate(d.getDate() + 1);
      const dayValid = orders.filter((o) => { const t = toDate(o); return t && t >= d && t < next && o.status !== 'cancelled'; });
      days.push({
        label: d.toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric' }),
        value: dayValid.reduce((sum, o) => sum + (Number(o.totalPrice) || 0), 0),
        sub: `${dayValid.length} sipariş`,
      });
    }

    const hours = Array.from({ length: 24 }, (_, h) => ({ label: `${String(h).padStart(2, '0')}:00`, value: 0 }));
    valid.forEach((o) => { const t = toDate(o); if (t) hours[t.getHours()].value += 1; });

    const products = {};
    valid.forEach((o) => (o.items || []).forEach((it) => {
      products[it.name] = (products[it.name] || 0) + (Number(it.quantity) || 0);
    }));
    const topProducts = Object.entries(products).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 8);

    const payments = {};
    valid.forEach((o) => { const p = o.paymentMethod || 'Diğer'; payments[p] = (payments[p] || 0) + 1; });

    return {
      total: inPeriod.length,
      validCount: valid.length,
      revenue,
      avg: valid.length ? revenue / valid.length : 0,
      cancelled: cancelled.length,
      cancelRate: inPeriod.length ? Math.round((cancelled.length / inPeriod.length) * 100) : 0,
      active: orders.filter((o) => ['pending', 'preparing', 'ready', 'on_the_way'].includes(o.status)).length,
      delivery: valid.filter((o) => o.orderType !== 'pickup').length,
      pickup: valid.filter((o) => o.orderType === 'pickup').length,
      days,
      hours,
      topProducts,
      payments: Object.entries(payments).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value),
      recent: [...orders].sort((a, b) => (toDate(b) || 0) - (toDate(a) || 0)).slice(0, 6),
    };
  }, [orders, period]);

  if (error) {
    return (
      <div className="k-page">
        <div className="k-alert error">{error} <button type="button" className="k-btn k-btn-secondary k-btn-sm" style={{ marginLeft: 12 }} onClick={fetchOrders}>Tekrar dene</button></div>
      </div>
    );
  }
  if (!s) return <div className="k-page"><Loading text="Veriler yükleniyor…" /></div>;

  const periodLabel = PERIODS.find(([id]) => id === period)[1].toLocaleLowerCase('tr-TR');

  return (
    <div className="k-page">
      <PageHead desc={new Date().toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}>
        <div className="k-seg">
          {PERIODS.map(([id, label]) => <button key={id} type="button" className={period === id ? 'on' : ''} onClick={() => setPeriod(id)}>{label}</button>)}
        </div>
        <button type="button" className="k-btn k-btn-secondary" onClick={fetchOrders}><Icon name="refresh" size={15} />Yenile</button>
      </PageHead>

      {s.active > 0 && (
        <button type="button" className="kd-live" onClick={() => onNavigate?.('orders')}>
          <span className="kd-live-dot" />
          <strong>{s.active} aktif sipariş</strong> şu an işlemde
          <span className="kd-live-go">Siparişlere git <Icon name="chevron" size={15} /></span>
        </button>
      )}

      <div className="kd-kpis">
        <div className="kd-kpi">
          <span className="kd-kpi-label"><Icon name="coin" size={15} />Ciro</span>
          <strong className="tnum">{tl(s.revenue)}</strong>
          <em>{periodLabel} · iptaller hariç</em>
        </div>
        <div className="kd-kpi">
          <span className="kd-kpi-label"><Icon name="orders" size={15} />Sipariş</span>
          <strong className="tnum">{s.validCount}</strong>
          <em>{s.delivery} teslimat · {s.pickup} gel-al</em>
        </div>
        <div className="kd-kpi">
          <span className="kd-kpi-label"><Icon name="bag" size={15} />Ortalama sepet</span>
          <strong className="tnum">{s.validCount ? tl(s.avg) : '—'}</strong>
          <em>sipariş başına</em>
        </div>
        <div className="kd-kpi">
          <span className="kd-kpi-label"><Icon name="x" size={15} />İptal oranı</span>
          <strong className="tnum">%{s.cancelRate}</strong>
          <em>{s.cancelled} iptal / {s.total} sipariş</em>
        </div>
      </div>

      <div className="kd-grid">
        <Card title="Son 7 gün ciro" sub="Günlük, iptaller hariç" className="kd-span-2">
          <BarChart data={s.days} format={(v) => tl(v)} emptyText="Son 7 günde satış yok" />
        </Card>
        <Card title="Ödeme yöntemleri" sub={periodLabel}>
          {s.payments.length ? <HBars rows={s.payments} format={(v) => `${v} sipariş`} /> : <Empty icon="coin" title="Henüz veri yok" />}
        </Card>
        <Card title="Yoğun saatler" sub={`Saat başına sipariş · ${periodLabel}`} className="kd-span-2">
          <BarChart data={s.hours} format={(v) => `${v} sipariş`} height={150} labelEvery={3} integer emptyText="Bu dönemde sipariş yok" />
        </Card>
        <Card title="En çok satanlar" sub={`Adet · ${periodLabel}`}>
          {s.topProducts.length ? <HBars rows={s.topProducts} format={(v) => `${v} adet`} /> : <Empty icon="dish" title="Henüz satış yok" />}
        </Card>
        <Card title="Son siparişler" className="kd-span-3" actions={<button type="button" className="k-btn k-btn-ghost k-btn-sm" onClick={() => onNavigate?.('orders')}>Tümü <Icon name="chevron" size={14} /></button>} pad={false}>
          {s.recent.length === 0 ? <Empty title="Henüz sipariş yok">İlk sipariş geldiğinde burada görünecek.</Empty> : (
            <table className="k-table">
              <thead><tr><th>Sipariş</th><th>Müşteri</th><th>Tür</th><th>Tutar</th><th>Durum</th><th>Zaman</th></tr></thead>
              <tbody>
                {s.recent.map((o) => (
                  <tr key={o.id} className="clickable" onClick={() => onNavigate?.('orders')}>
                    <td className="tnum" style={{ fontWeight: 700 }}>#{o.id.substring(0, 6).toUpperCase()}</td>
                    <td>{o.userName || '—'}</td>
                    <td>{o.orderType === 'pickup' ? 'Gel-al' : 'Teslimat'}</td>
                    <td className="tnum" style={{ fontWeight: 700 }}>{tl(o.totalPrice)}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="tnum" style={{ color: 'var(--k-muted)' }}>{toDate(o)?.toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
